import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {NucleotideType} from 'biojs/BioConstants';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {GenomeOption} from 'constants/GenomeConstants';
import {DNA_SEQUENCE_IMPORT_FLOW} from 'molbio/MolBioConstants';
import notifications from 'notifications';
import ClearSearchButton from 'search/ClearSearchButton';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {geneToName, transcriptToName} from 'util/GenomeUtil';
import * as keyboard from 'util/Keyboard';
import {EllipsisDirection, ellipsize} from 'util/NameUtil';
import {isServerRequestError} from 'util/Net';

import DBImportItems from './DBImportItems';
import GenomeSearchSection from './GenomeSearchSection';
import {
  DBHandle,
  DBImportItemOptions,
  DBImportSequence,
  DBQueryResponse,
  SequenceImportOptions,
} from './ImportSequenceConstants';
import {queryAccessionsFromDB} from './ImportSequenceDAO';
import SequenceImportOptionsForm from './SequenceImportOptionsForm';

const EXAMPLE_QUERY_DATA = [
  {
    example: 'https://www.addgene.org/browse/sequence/419908/',
    source: 'Addgene URL',
  },
  {
    example: 'BRCA2',
    source: 'Gene name',
  },
  {
    example: 'M62653',
    source: 'NCBI Accession',
  },
  {
    example: 'ENSMUSG00000041147',
    source: 'ENSEMBL ID',
  },
  {
    example: 'BBa_E0040',
    source: 'Registry of Standard Biological Parts',
  },
  {
    example: 'JPUB_001430',
    source: 'JBEI Public Registry',
  },
];

// Keep in sync with the regex used in test_query functions in benchling/pybio/seqio/connectors/
const SOURCE_FORMAT_REGEX = [
  /^(ENS)([A-Z]{4}|[A-Z]{1})\d{11}(\.\d)?/, // Ensembl
  // Addgene
  /(?:https?:\/\/)?(?:www.)?addgene.org\/(\d+)/,
  /(?:https?:\/\/)?(?:www.)?addgene.org\/browse\/sequence\/(\d+)/,
  /(?:https?:\/\/)?(?:www.)?addgene.org\/vector-database\/(\d+)/,
  /(?:https?:\/\/)?(?:www.)?addgene.org\/browse\/sequence_vdb\/(\d+)/,
  /^JPUB_\d{6}$/, // JBEI
  // NCBI
  /^[a-zA-Z]{1}_?\d{5}(?:\.\d+)?/,
  /^[a-zA-Z]{2}_?\d{6}(?:\.\d+)?/,
  // Parts registry
  /BBa_[a-z]\d{4,}/,
  /pSB\d+[a-z]+\d+/,
];

// If a search is at least seven characters long and consists entirely of bases, reject it out of hand.
const ALL_BASES_REGEX: RegExp = /^[ACGTU]{7,}$/i;

export interface DBImportFormProps {
  queryResults: Array<DBImportSequence>;
  setQueryResults: (results: Array<DBImportSequence>) => void;
  sequenceImportOptions: SequenceImportOptions;
  setSequenceImportOptions: (options: SequenceImportOptions) => void;
}

interface QueryChip {
  query: string;
  error?: string;
}

function makeAllBasesError(query: string): string {
  return `${ellipsize(query, 15, EllipsisDirection.MIDDLE)} is not a valid accession identifier or gene name from a supported database.`;
}

function makeQueryChip(query: string): QueryChip {
  if (ALL_BASES_REGEX.test(query)) {
    return {query, error: makeAllBasesError(query)};
  } else {
    return {query};
  }
}

function getBannerProps(
  queryChipValues: Array<QueryChip>
): Pick<InlineBannerProps, 'bannerType' | 'text'> | null {
  // If we don't have a query yet, we have nothing to validate
  if (queryChipValues.length === 0) {
    return null;
  }
  const errorCount = queryChipValues.reduce((sum, queryChip) => {
    if (queryChip.error != null) {
      sum++;
    }
    return sum;
  }, 0);
  if (errorCount === queryChipValues.length) {
    return {
      bannerType: InlineBannerType.ERROR,
      text: 'None of the specified queries are valid accession identifiers or gene names from a supported database',
    };
  } else if (errorCount > 0) {
    return {
      bannerType: InlineBannerType.WARNING,
      text: 'Some of the specified queries are not valid accession identifiers or gene names from a supported database. These queries will be ignored.',
    };
  }
  return null;
}

function DBImportForm({
  queryResults,
  setQueryResults,
  sequenceImportOptions,
  setSequenceImportOptions,
}: DBImportFormProps): JSX.Element {
  const [inputQuery, setInputQuery] = useState<QueryChip>({query: ''});
  const [queryChipValues, setQueryChipValues] = useState<Array<QueryChip>>([]);
  const [unknownKeywords, setUnknownKeywords] = useState<Array<string>>([]);
  const [species, setSpecies] = useState<GenomeOption | undefined>(undefined);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dismissedIGemWarning, setDismissedIGemWarning] = useState(false);

  const hasGenomicSequences = queryResults.some(
    ({resultData, importOptions}) =>
      importOptions.isSelected &&
      !importOptions.asCDNA &&
      [DBHandle.ENSEMBL, DBHandle.PRIVATE].includes(resultData.dbHandle)
  );

  const queryChipBannerProps = getBannerProps(queryChipValues);

  useEffect(() => {
    // If any of the queries do not consist entirely of bases and do not match any of our standard source
    // formats, treat that query as an Ensembl keyword query and require the user to select a species.
    const unknownAccessionFormats = [...queryChipValues, inputQuery].filter(
      ({query}: {query: string}) =>
        query.length > 0 &&
        !SOURCE_FORMAT_REGEX.some((term: RegExp) => new RegExp(term, 'i').test(query)) &&
        !ALL_BASES_REGEX.test(query)
    );
    setUnknownKeywords([...new Set(unknownAccessionFormats.map((chip) => chip.query))]);
  }, [queryChipValues, inputQuery]);

  function getAllQueryTerms(): Array<QueryChip> {
    return inputQuery.query.length === 0 ? queryChipValues : [...queryChipValues, inputQuery];
  }

  function handleClickExample(example: string): void {
    if (example === 'BRCA2') {
      setQueryChipValues([{query: example}]);
      setInputQuery({query: ''});
    } else {
      submitSearchAccessions([{query: example}]);
    }
  }

  function getInitialImportOptions(info: DBQueryResponse): DBImportItemOptions {
    if (info.versions != null) {
      const firstVersionTranscripts = info.versions[0].transcripts;
      const defaultTranscript =
        firstVersionTranscripts.find((transcript) => transcript.default) ??
        firstVersionTranscripts[0] ??
        null;
      const transcriptValue =
        info._dbhandle === DBHandle.ENSEMBL ? defaultTranscript?.ensemblId : defaultTranscript?.id;

      return {
        isSelected: true,
        asCDNA: false,
        revcomp: info.versions[0].gene.strand === -1,
        selectedVersionIndex: 0,
        accession: {
          upstream: 0,
          downstream: 0,
          transcriptIds: transcriptValue != null ? [transcriptValue] : [],
        },
        name:
          defaultTranscript != null
            ? transcriptToName({...defaultTranscript, id: '', ccdsId: null, refseqId: ''})
            : geneToName(info.versions[0].gene),
      };
    }
    return {
      accession: info.acc,
      name: info.name,
      isSelected: !(
        info._dbhandle === DBHandle.ADDGENE &&
        (info._sequences == null || info._sequences.length === 0)
      ),
    };
  }

  async function submitSearchAccessions(queries: Array<QueryChip>): Promise<void> {
    const queryChipsWithErrors = queries.map(({query}) => makeQueryChip(query));
    setQueryChipValues(queryChipsWithErrors);
    setInputQuery({query: ''});
    if (queryChipsWithErrors.every((query) => query.error != null)) {
      // If every chip has an error, short-circuit and allow the query chips that have just been flushed to state
      // to disable the search button.
      return;
    }
    const queryString = queryChipsWithErrors.map(({query}) => query).join(',');
    analytics.track('sequence', 'select_files', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.DB,
      query: queryString,
      genome: species?.speciesName,
      liveEnsemblUI: true,
    });
    setErrorMessage(null);
    setIsSearching(true);
    setQueryResults([]);
    try {
      const {accessionInfos} = await queryAccessionsFromDB(
        queryString,
        // For Ensembl genomes, the species name is used for Ensembl's symbol search
        // For internal genomes, the Ensembl name is used to filter within Benchling
        species?.hidden != null ? species?.ensemblName : species?.speciesName
      );
      const results: Array<DBImportSequence> = accessionInfos.map((info: DBQueryResponse) => {
        const {_dbhandle, _dbimg, _dbname, _link, _sequences, ...restInfo} = info;
        return {
          resultData: {
            ...restInfo,
            dbHandle: _dbhandle,
            dbName: _dbname,
            dbImg: _dbimg,
            accLink: _link,
            sequences: _sequences,
          },
          importOptions: getInitialImportOptions(info),
        };
      });
      setQueryResults(results);
    } catch (error) {
      // If there were no results found, we show a warning in the modal instead of a toast
      if (
        !(error.status === 404 || (error.status === 500 && error.userMessage.startsWith('Login required')))
      ) {
        notifications.notifyError(error);
      }
      if (isServerRequestError(error)) {
        setErrorMessage(notifications.getFriendlyError(error));
      } else {
        throw error;
      }
    } finally {
      setIsSearching(false);
    }
  }

  function handleAccessionsChange(inputValue: string): void {
    setErrorMessage(null);
    const queryList = inputValue.split(/(?:,| )+/).map((keyword) => keyword.trim());
    if (queryList.length === 0) {
      setInputQuery({query: ''});
      return;
    }
    setQueryChipValues([
      ...queryChipValues,
      ...queryList
        .slice(0, -1)
        .filter((keyword) => keyword.length > 0)
        .map((query) => makeQueryChip(query)),
    ]);
    setInputQuery(makeQueryChip(queryList[queryList.length - 1]));
  }

  function clearSearch(): void {
    setErrorMessage(null);
    setSpecies(undefined);
    setInputQuery({query: ''});
    setQueryChipValues([]);
    setQueryResults([]);
  }

  function updateImportOptions(newOptions: Partial<DBImportItemOptions>, resultIndex: number): void {
    const {resultData, importOptions} = queryResults[resultIndex];
    const newSequenceItem = {resultData, importOptions: {...importOptions, ...newOptions}};
    setQueryResults([
      ...queryResults.slice(0, resultIndex),
      newSequenceItem,
      ...queryResults.slice(resultIndex + 1),
    ]);
    if (
      newOptions.hasOwnProperty('asCDNA') &&
      !newOptions.asCDNA &&
      sequenceImportOptions.nucleotideType !== NucleotideType.DNA
    ) {
      setSequenceImportOptions({
        ...sequenceImportOptions,
        nucleotideType: NucleotideType.DNA,
        tagSchemaId: undefined,
      });
    }
  }

  function updateNucleotideType(newNucleotideType: NucleotideType): void {
    analytics.track('sequence', 'toggle_nucleotide_type', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.DB,
      nucleotideType: newNucleotideType,
      liveEnsemblUI: true,
    });

    if (newNucleotideType === NucleotideType.RNA) {
      // RNA sequences imported as cDNA must be imported in the sense direction
      // if they are in the reverse direction
      const newQueryResults = queryResults.map(({resultData, importOptions}) => {
        if ([DBHandle.ENSEMBL, DBHandle.PRIVATE].includes(resultData.dbHandle)) {
          const selectedVersion = resultData.versions![importOptions.selectedVersionIndex!];
          const {gene} = selectedVersion;
          return {
            resultData,
            importOptions: {...importOptions, revcomp: gene.strand === -1 && importOptions.asCDNA},
          };
        } else {
          return {resultData, importOptions};
        }
      });
      setQueryResults(newQueryResults);
    }
    setSequenceImportOptions({
      ...sequenceImportOptions,
      nucleotideType: newNucleotideType,
      tagSchemaId: undefined,
    });
  }

  function onSelectGenome(newGenome: GenomeOption): void {
    analytics.track('sequence', 'change_genome', {
      product: ANALYTICS_MODULE.MOLBIO,
      importFlow: DNA_SEQUENCE_IMPORT_FLOW.DB,
      genome: newGenome.ensemblName,
      liveEnsemblUI: true,
    });
    setSpecies(newGenome);
  }

  return (
    <>
      {queryChipValues.length === 0 && inputQuery.query.length === 0 && (
        <div>
          <p>Example searches:</p>
          <ul className={css(styles.exampleQueryList)}>
            {EXAMPLE_QUERY_DATA.map(({example, source}) => (
              <li key={source}>
                <a
                  className={css(styles.exampleQueryLink)}
                  onClick={() => {
                    handleClickExample(example);
                  }}
                >
                  {example}
                </a>
                <span>{` (${source})`}</span>
              </li>
            ))}
          </ul>
          <p>
            Import multiple sequences at once by entering space-separated or comma-separated accession
            numbers.
          </p>
        </div>
      )}

      <FormGroup>
        {!dismissedIGemWarning && (
          <div className={css(styles.bannerWrapper)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="Imports from iGEM may not work due to limitations with their parts registry. Try uploading GenBank files instead."
              onDismiss={() => {
                setDismissedIGemWarning(true);
              }}
            />
          </div>
        )}
        {queryChipBannerProps != null && (
          <div className={css(styles.bannerWrapper)}>
            <InlineBanner {...queryChipBannerProps} />
          </div>
        )}
        {errorMessage != null && (
          <div className={css(styles.bannerWrapper)}>
            <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
          </div>
        )}
        <FieldLabel label="Sequence" />
        <div className={css(styles.searchSection)}>
          <ChipSelectInput
            className={css(styles.accessionSearchInput)}
            optionDatas={[]}
            value={queryChipValues}
            query={inputQuery.query}
            isMulti={true}
            autoFocus={true}
            getChipProps={(optionValue) => ({
              id: optionValue.query,
              text: optionValue.query,
              isLink: false,
              size: ChipSize.SMALL,
              negative: optionValue.error != null,
              tooltipTitle: optionValue.error ?? null,
            })}
            onBlur={() => {
              if (inputQuery.query.length > 0) {
                setQueryChipValues(getAllQueryTerms());
                setInputQuery({query: ''});
              }
            }}
            onQueryChange={(e) => {
              handleAccessionsChange(e.target.value);
            }}
            onRemove={(optionValue) => {
              setErrorMessage(null);
              if (!isSearching) {
                setQueryChipValues(queryChipValues.filter((chip) => chip !== optionValue));
              }
            }}
            onKeyDown={keyboard.callOnEnter(() => {
              if (inputQuery.query.length > 0) {
                setQueryChipValues(getAllQueryTerms());
                setInputQuery({query: ''});
              } else if (species != null || unknownKeywords.length === 0) {
                submitSearchAccessions(queryChipValues);
              }
            })}
            placeholder=""
            disabled={isSearching}
          />
          {unknownKeywords.length === 0 && (
            <Button
              className={css(styles.searchButton)}
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MEDIUM}
              text="Search"
              onClick={() => {
                submitSearchAccessions(getAllQueryTerms());
              }}
              disabled={
                isSearching ||
                (queryChipValues.length === 0 && inputQuery.query.length === 0) ||
                (queryChipValues.length > 0 && queryChipValues.every((queryChip) => queryChip.error != null))
              }
              dataTestAttrs={{component: 'DBImportSequence', element: 'button', key: 'search'}}
            />
          )}
          {unknownKeywords.length === 0 &&
            (queryChipValues.length !== 0 || inputQuery.query.length !== 0) &&
            !isSearching && (
              <div className={css(styles.clearSearchButton)}>
                <ClearSearchButton onClear={clearSearch} />
              </div>
            )}
        </div>
      </FormGroup>

      {unknownKeywords.length > 0 && (
        <>
          <p className={css(styles.speciesHelpText)}>
            {'Enter a species name for '}
            <span className={css(styles.italicHelpText)}>{`${unknownKeywords.join(', ')}`}</span>
            {' to search through Ensembl. If no exact matches are found, species will be ignored.'}
          </p>
          <GenomeSearchSection
            selectedGenome={species}
            onSelectGenome={onSelectGenome}
            isSearching={isSearching}
            disableSearch={
              queryChipBannerProps != null && queryChipBannerProps.bannerType === InlineBannerType.ERROR
            }
            canClearSelection={queryChipValues.length !== 0 || inputQuery.query.length !== 0}
            onClear={clearSearch}
            onSearch={() => {
              submitSearchAccessions(getAllQueryTerms());
            }}
          />
        </>
      )}

      {isSearching ? (
        <div {...getTestProps({component: 'DBImportSequence', element: 'spinner'})}>
          <CenteredHorizontalThrobber suppressPadding={true} />
        </div>
      ) : (
        <DBImportItems
          sequences={queryResults}
          updateImportOptions={updateImportOptions}
          sequenceImportOptions={sequenceImportOptions}
        />
      )}

      {queryResults.length > 0 && (
        <SequenceImportOptionsForm
          multipleSequences={queryResults.length > 1}
          sequenceImportOptions={sequenceImportOptions}
          folderIdOptions={{
            onChange: (folderId) => {
              setSequenceImportOptions({...sequenceImportOptions, folderId});
            },
          }}
          nucleotideTypeOptions={{
            isDisabled: hasGenomicSequences,
            disabledHelpText: 'Genomic sequences may only be imported as DNA.',
            onChange: updateNucleotideType,
          }}
          tagSchemaIdOptions={{
            onChange: (tagSchemaId) => {
              setSequenceImportOptions({...sequenceImportOptions, tagSchemaId});
            },
          }}
          studySearchItemOptions={{
            onChange: (studySearchItems) => {
              setSequenceImportOptions({...sequenceImportOptions, studySearchItems});
            },
          }}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  exampleQueryList: {
    paddingInlineStart: '24px',
  },
  exampleQueryLink: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  searchSection: {
    display: 'flex',
  },
  accessionSearchInput: {
    width: '50%',
  },
  searchButton: {
    marginLeft: '16px',
  },
  clearSearchButton: {
    margin: '10px 0 0 18px',
  },
  speciesHelpText: {
    marginTop: '6px',
  },
  italicHelpText: {
    fontStyle: 'italic',
  },
  bannerWrapper: {
    marginBottom: '16px',
  },
});

export default DBImportForm;
